import {
  calendarOutline,
  calendar,
  peopleOutline,
  people,
  clipboardOutline,
  clipboard,
  cashOutline,
  cash,
  receiptOutline,
  receipt,
  calculatorOutline,
  calculator,
  constructOutline,
  construct,
  chatbubblesOutline,
  notificationsOutline,
  notifications,
  trendingUpOutline,
  barChartOutline,
  barChart,
  statsChartOutline,
  personOutline,
  person,
  settingsOutline,
  settings,
  peopleCircleOutline,
  peopleCircle,
  cloudDownloadOutline,
  cloudDownload,
  timerOutline,
} from 'ionicons/icons';
import type { Shortcut } from '@rsmaids/shared/types/dashboard';
import i18n from '../../i18n';

// Single source of truth for every dashboard's shortcut catalogue. Each
// dashboard renders its own catalogue in the swiper + edit modal, and
// AppSettings picks the matching one by role so the in-Settings shortcut
// editor always offers the options for the user's actual dashboard.

const STANDARD_CATEGORY_NAMES: Record<string, string> = {
  get core() { return i18n.t('pages.dashboard.shortcutCatalogues.coreFeatures'); },
  get business() { return i18n.t('pages.dashboard.shortcutCatalogues.businessTools'); },
  get communication() { return i18n.t('pages.dashboard.shortcutCatalogues.communication'); },
  get analytics() { return i18n.t('pages.dashboard.shortcutCatalogues.analyticsReports'); },
  get admin() { return i18n.t('pages.dashboard.shortcutCatalogues.administration'); },
};

const isDesktop = import.meta.env.VITE_PLATFORM === 'desktop';

export const OWNER_SHORTCUTS: Shortcut[] = [
  { id: 'calendar', icon: calendarOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.schedule'); }, route: isDesktop ? '/tabs/schedule' : '/tabs/calendar', category: 'core' },
  { id: 'contacts', icon: peopleOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.contacts'); }, route: '/tabs/contacts', category: 'core' },
  { id: 'bookings', icon: clipboardOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.bookings'); }, route: '/tabs/bookings-list', category: 'core' },
  { id: 'payments', icon: cashOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.payments'); }, route: '/tabs/payments', category: 'business' },
  { id: 'invoices', icon: receiptOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.invoices'); }, route: '/tabs/invoices', category: 'business' },
  { id: 'estimates', icon: calculatorOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.estimates'); }, route: '/tabs/estimates', category: 'business' },
  { id: 'services', icon: constructOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.services'); }, route: '/tabs/service-types', category: 'business' },
  { id: 'messages', icon: chatbubblesOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.messages'); }, route: '/team/messages', category: 'communication' },
  { id: 'alerts', icon: notificationsOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.alerts'); }, route: '/tabs/dashboard/owner/alerts', category: 'communication' },
  { id: 'analytics', icon: trendingUpOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.analytics'); }, route: '/tabs/analytics', category: 'analytics' },
  { id: 'reports', icon: barChartOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.reports'); }, route: '/reports', category: 'analytics' },
  { id: 'profile', icon: personOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.profile'); }, route: '/tabs/profile', category: 'admin' },
  { id: 'settings', icon: settingsOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.settings'); }, route: '/tabs/dashboard/owner/settings', category: 'admin' },
  { id: 'team', icon: peopleCircleOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.team'); }, route: '/team/members', category: 'admin' },
  { id: 'backup', icon: cloudDownloadOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.backup'); }, route: '/backup', category: 'admin' },
  { id: 'notifications', icon: notificationsOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.notifications'); }, route: '/tabs/dashboard/owner/notifications', category: 'admin' },
];

export const FINANCE_SHORTCUTS: Shortcut[] = [
  { id: 'calendar', icon: calendar, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.schedule'); }, route: isDesktop ? '/tabs/schedule' : '/tabs/calendar', category: 'core' },
  { id: 'contacts', icon: people, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.contacts'); }, route: '/tabs/contacts', category: 'core' },
  { id: 'bookings', icon: clipboard, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.bookings'); }, route: '/tabs/bookings-list', category: 'core' },
  { id: 'invoices', icon: receipt, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.invoices'); }, route: '/invoices', category: 'core' },
  { id: 'payments', icon: cash, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.payments'); }, route: '/payments', category: 'business' },
  { id: 'estimates', icon: calculator, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.estimates'); }, route: '/tabs/estimates', category: 'business' },
  { id: 'services', icon: construct, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.services'); }, route: '/tabs/service-types', category: 'business' },
  { id: 'messages', icon: chatbubblesOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.messages'); }, route: '/dashboard/team-messaging', category: 'communication' },
  { id: 'alerts', icon: notifications, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.alerts'); }, route: '/tabs/dashboard/finance/alerts', category: 'communication' },
  { id: 'reports', icon: barChart, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.reports'); }, route: '/reports', category: 'analytics' },
  { id: 'profile', icon: person, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.profile'); }, route: '/profile', category: 'admin' },
  { id: 'settings', icon: settings, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.settings'); }, route: '/tabs/settings', category: 'admin' },
  { id: 'team', icon: peopleCircle, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.team'); }, route: '/team/members', category: 'admin' },
  { id: 'backup', icon: cloudDownload, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.backup'); }, route: '/backup', category: 'admin' },
];

export const SALES_SHORTCUTS: Shortcut[] = [
  { id: 'calendar', icon: calendar, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.schedule'); }, route: isDesktop ? '/tabs/schedule' : '/tabs/calendar', category: 'core' },
  { id: 'contacts', icon: people, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.contacts'); }, route: '/tabs/contacts', category: 'core' },
  { id: 'bookings', icon: clipboard, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.bookings'); }, route: '/tabs/bookings-list', category: 'core' },
  { id: 'leads', icon: peopleOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.leads'); }, route: '/tabs/leads', category: 'core' },
  { id: 'payments', icon: cash, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.payments'); }, route: '/payments', category: 'business' },
  { id: 'invoices', icon: receipt, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.invoices'); }, route: '/tabs/invoices', category: 'business' },
  { id: 'estimates', icon: calculator, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.estimates'); }, route: '/tabs/estimates', category: 'business' },
  { id: 'services', icon: construct, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.services'); }, route: '/tabs/service-types', category: 'business' },
  { id: 'messages', icon: chatbubblesOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.messages'); }, route: '/team/messages', category: 'communication' },
  { id: 'alerts', icon: notifications, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.alerts'); }, route: '/tabs/dashboard/sales/alerts', category: 'communication' },
  { id: 'analytics', icon: statsChartOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.analytics'); }, route: '/tabs/analytics', category: 'analytics' },
  { id: 'reports', icon: barChart, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.reports'); }, route: '/reports', category: 'analytics' },
  { id: 'profile', icon: person, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.profile'); }, route: '/tabs/profile', category: 'admin' },
  { id: 'settings', icon: settings, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.settings'); }, route: '/tabs/settings', category: 'admin' },
  { id: 'team', icon: peopleCircle, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.team'); }, route: '/team/members', category: 'admin' },
  { id: 'backup', icon: cloudDownload, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.backup'); }, route: '/backup', category: 'admin' },
];

export const MLS_SHORTCUTS: Shortcut[] = [
  { id: 'schedule', icon: calendarOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.mySchedule'); }, route: '/tabs/my-schedule', category: 'core' },
  { id: 'timesheet', icon: timerOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.timesheet'); }, route: '/tabs/my-schedule', category: 'core' },
  { id: 'pay', icon: cashOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.payEarnings'); }, route: '/tabs/profile', category: 'core' },
  { id: 'profile', icon: person, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.profile'); }, route: '/tabs/profile', category: 'core' },
  { id: 'alerts', icon: notificationsOutline, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.alerts'); }, route: '/tabs/dashboard/mls/alerts', category: 'communication' },
  { id: 'performance', icon: barChart, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.performance'); }, route: '/tabs/profile', category: 'analytics' },
  { id: 'settings', icon: settings, get title() { return i18n.t('pages.dashboard.shortcutCatalogues.settings'); }, route: '/tabs/profile', category: 'admin' },
];

export const OWNER_CATEGORY_NAMES = STANDARD_CATEGORY_NAMES;
export const FINANCE_CATEGORY_NAMES = STANDARD_CATEGORY_NAMES;
export const SALES_CATEGORY_NAMES = STANDARD_CATEGORY_NAMES;
export const MLS_CATEGORY_NAMES: Record<string, string> = {
  get core() { return i18n.t('pages.dashboard.shortcutCatalogues.coreFeatures'); },
  get communication() { return i18n.t('pages.dashboard.shortcutCatalogues.communication'); },
  get analytics() { return i18n.t('pages.dashboard.shortcutCatalogues.analytics'); },
  get admin() { return i18n.t('pages.dashboard.shortcutCatalogues.settings'); },
};

export const OWNER_DEFAULT_SHORTCUT_IDS = ['leads', 'analytics', 'profile', 'alerts', 'settings'];
export const FINANCE_DEFAULT_SHORTCUT_IDS = ['invoices', 'reports', 'profile', 'alerts', 'settings'];
export const SALES_DEFAULT_SHORTCUT_IDS = ['leads', 'analytics', 'profile', 'alerts', 'settings'];
export const MLS_DEFAULT_SHORTCUT_IDS = ['schedule', 'timesheet', 'pay', 'profile'];

export type DashboardKind = 'owner' | 'finance' | 'sales' | 'mls';

export interface ShortcutCatalogue {
  shortcuts: Shortcut[];
  categoryNames: Record<string, string>;
  defaultIds: string[];
  localStorageKey: string;
}

export const SHORTCUT_CATALOGUES: Record<DashboardKind, ShortcutCatalogue> = {
  owner: {
    shortcuts: OWNER_SHORTCUTS,
    categoryNames: OWNER_CATEGORY_NAMES,
    defaultIds: OWNER_DEFAULT_SHORTCUT_IDS,
    localStorageKey: 'dashboard-owner-shortcuts',
  },
  finance: {
    shortcuts: FINANCE_SHORTCUTS,
    categoryNames: FINANCE_CATEGORY_NAMES,
    defaultIds: FINANCE_DEFAULT_SHORTCUT_IDS,
    localStorageKey: 'dashboard-finance-shortcuts',
  },
  sales: {
    shortcuts: SALES_SHORTCUTS,
    categoryNames: SALES_CATEGORY_NAMES,
    defaultIds: SALES_DEFAULT_SHORTCUT_IDS,
    localStorageKey: 'dashboard-sales-shortcuts',
  },
  mls: {
    shortcuts: MLS_SHORTCUTS,
    categoryNames: MLS_CATEGORY_NAMES,
    defaultIds: MLS_DEFAULT_SHORTCUT_IDS,
    localStorageKey: 'dashboard-mls-shortcuts',
  },
};

// Map a user's fine-grained role (user.role: 'owner' | 'admin' | 'manager' |
// 'salesManager' | 'financeManager' | 'lead' | 'staff' | 'client') to the
// shortcut catalogue for the dashboard that role actually sees. Returns null
// when that role has no shortcut-editing dashboard (manager, client).
export const catalogueForRole = (role: string | undefined): ShortcutCatalogue | null => {
  switch (role) {
    case 'owner':
    case 'admin':
      return SHORTCUT_CATALOGUES.owner;
    case 'salesManager':
      return SHORTCUT_CATALOGUES.sales;
    case 'financeManager':
      return SHORTCUT_CATALOGUES.finance;
    case 'lead':
    case 'staff':
      return SHORTCUT_CATALOGUES.mls;
    default:
      return null;
  }
};
